export interface CountryCode {
    name: string;
    code: string; // Country code like US, UK, etc.
    dialCode: string; // Phone dial code like +1, +44, etc.
    flag: string; // Flag emoji
}

// REST Countries API response interface
interface RestCountryAPIResponse {
    name: {
        common: string;
        official: string;
    };
    cca2: string;
    idd: {
        root: string;
        suffixes?: string[];
    };
    flag: string;
}

// Cache for countries data
let countriesCache: CountryCode[] | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Fallback countries for offline use
const FALLBACK_COUNTRIES: CountryCode[] = [
    { name: "United States", code: "US", dialCode: "+1", flag: "🇺🇸" },
    { name: "United Kingdom", code: "GB", dialCode: "+44", flag: "🇬🇧" },
    { name: "Canada", code: "CA", dialCode: "+1", flag: "🇨🇦" },
    { name: "Australia", code: "AU", dialCode: "+61", flag: "🇦🇺" },
    { name: "Germany", code: "DE", dialCode: "+49", flag: "🇩🇪" },
    { name: "France", code: "FR", dialCode: "+33", flag: "🇫🇷" },
    { name: "Italy", code: "IT", dialCode: "+39", flag: "🇮🇹" },
    { name: "Spain", code: "ES", dialCode: "+34", flag: "🇪🇸" },
    { name: "Netherlands", code: "NL", dialCode: "+31", flag: "🇳🇱" },
    { name: "Brazil", code: "BR", dialCode: "+55", flag: "🇧🇷" },
    { name: "India", code: "IN", dialCode: "+91", flag: "🇮🇳" },
    { name: "China", code: "CN", dialCode: "+86", flag: "🇨🇳" },
    { name: "Japan", code: "JP", dialCode: "+81", flag: "🇯🇵" },
    { name: "South Korea", code: "KR", dialCode: "+82", flag: "🇰🇷" }
];

// Fetch countries from REST Countries API
const fetchCountriesFromAPI = async (): Promise<CountryCode[]> => {
    try {
        const response = await fetch('https://restcountries.com/v3.1/all?fields=name,cca2,idd,flag');

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: RestCountryAPIResponse[] = await response.json();

        const countries: CountryCode[] = data
            .map((country) => {
                // Construct dial code from root and suffixes
                let dialCode = country.idd.root || '';
                if (country.idd.suffixes && country.idd.suffixes.length > 0) {
                    // Use the first suffix for simplicity
                    dialCode += country.idd.suffixes[0];
                }

                // Skip countries without dial codes (like Antarctica)
                if (!dialCode || dialCode === '') {
                    return null;
                }

                return {
                    name: country.name.common,
                    code: country.cca2,
                    dialCode,
                    flag: country.flag
                };
            })
            .filter((country): country is CountryCode => country !== null)
            .sort((a, b) => a.name.localeCompare(b.name));

        return countries;
    } catch (error) {
        console.warn('Failed to fetch countries from API, using fallback data:', error);
        return FALLBACK_COUNTRIES;
    }
};

// Get all country codes (with caching)
export const getCountryCodes = async (): Promise<CountryCode[]> => {
    const now = Date.now();

    // Return cached data if available and fresh
    if (countriesCache && (now - cacheTimestamp) < CACHE_DURATION) {
        return countriesCache;
    }

    // Fetch fresh data
    try {
        const countries = await fetchCountriesFromAPI();
        countriesCache = countries;
        cacheTimestamp = now;

        // Store in localStorage for offline access
        if (typeof window !== 'undefined') {
            localStorage.setItem('countriesCache', JSON.stringify({
                data: countries,
                timestamp: now
            }));
        }

        return countries;
    } catch (error) {
        // Try to load from localStorage if API fails
        if (typeof window !== 'undefined') {
            try {
                const cached = localStorage.getItem('countriesCache');
                if (cached) {
                    const { data, timestamp } = JSON.parse(cached);
                    if (data && Array.isArray(data) && (now - timestamp) < (7 * 24 * 60 * 60 * 1000)) { // 7 days max for localStorage
                        countriesCache = data;
                        return data;
                    }
                }
            } catch (storageError) {
                console.warn('Failed to load countries from localStorage:', storageError);
            }
        }

        // Final fallback
        return FALLBACK_COUNTRIES;
    }
};

// Synchronous function for immediate access (uses cache or fallback)
export const COUNTRY_CODES = FALLBACK_COUNTRIES;

// Helper function to search countries by name or dial code
export const searchCountries = (query: string, countries: CountryCode[] = COUNTRY_CODES): CountryCode[] => {
    if (!query) return countries;

    const lowercaseQuery = query.toLowerCase();
    return countries.filter(country =>
        country.name.toLowerCase().includes(lowercaseQuery) ||
        country.dialCode.includes(query) ||
        country.code.toLowerCase().includes(lowercaseQuery)
    );
};

// Get country by dial code (async version with fresh data)
export const getCountryByDialCode = async (dialCode: string): Promise<CountryCode | undefined> => {
    const countries = await getCountryCodes();
    return countries.find(country => country.dialCode === dialCode);
};

// Get country by dial code (sync version with fallback data)
export const getCountryByDialCodeSync = (dialCode: string): CountryCode | undefined => {
    return COUNTRY_CODES.find(country => country.dialCode === dialCode);
};

// Get country by country code (async version with fresh data)
export const getCountryByCode = async (code: string): Promise<CountryCode | undefined> => {
    const countries = await getCountryCodes();
    return countries.find(country => country.code === code);
};

// Get country by country code (sync version with fallback data)
export const getCountryByCodeSync = (code: string): CountryCode | undefined => {
    return COUNTRY_CODES.find(country => country.code === code);
};

// Default country (can be changed based on user's location)
export const DEFAULT_COUNTRY = COUNTRY_CODES.find(country => country.code === "US") || COUNTRY_CODES[0];